<template>
	<div class="session-list">
		<LayoutBox class="session-list__container">
			<header class="session-list__header">
				<h4 class="session-list__title">Manage Session</h4>
				<AppTextField
					:placeholder="'Search'"
					icon-right="search"
					v-model="params.keyword"
					@input="searchItem"
					:disabled="loading"
				/>
			</header>
			<header class="session-list__header">
				<AppDropdown
					:placeholder="'Filter'"
					displayProp="text"
					:options="filterOptions"
					v-model="params.filter_by"
					@input="changeParams"
					:disabled="loading"
				/>

				<AppDropdown
					:placeholder="'Sort'"
					displayProp="text"
					:options="sortOptions"
					v-model="params.order_by"
					@input="changeParams"
					:disabled="loading"
				/>

                <div v-if="selected.length > 1">
					<AppButton
                        v-if="selected.every(i => i.status === 2)"
                        modifier="small-primary"
						@click="resumeSession(selected.map(i => i.id))"
						:disabled="buttonLoading"
                    >
                        Resume
                    </AppButton>

                    <AppButton
                        v-if="selected.every(i => i.status === 1)"
                        modifier="small-warning"
						@click="pauseSession(selected.map(i => i.id))"
						:disabled="buttonLoading"
                    >
                        Pause
                    </AppButton>

                    <AppButton
                        v-if="selected.every(i => i.status === 1 || i.status === 2)"
                        modifier="small-danger"
						:disabled="buttonLoading"
						@click="stopSession(selected.map(i => i.id))"
                    >
                        Stop
                    </AppButton>

                    <AppButton
                        v-if="selected.every(i => i.status === 0)"
                        modifier="small-danger"
						:disabled="buttonLoading"
						@click="archiveSession(selected.map(i => i.id))"
                    >
                        Delete
                    </AppButton>
                </div>

			</header>

			<AppTable
				class="session-list__table"
				:loading="loading"
				:fields="fields"
				:dataset="sessionList.data"
                :selected="selected"
				@select="selected = [...$event]"
				@click="viewSession"
			>
				<template #loading>
					<AppLoading/>
				</template>
				<template #empty>
					<AppTableEmpty>
						<h4>No data available</h4>
						<p>Your session may not yet started or already ended.</p>
					</AppTableEmpty>
				</template>
				<template #session_start="{ props: { item } }">
					<span v-if="item.session_start">{{ item.session_start | dateTime('lll') }}</span>
				</template>
				<template #session_end="{ props: { item } }">
					<span v-if="item.session_end">{{ item.session_end | dateTime('lll') }}</span>
				</template>
				<template #participants="{ props: { item } }">
					{{ item.participants.toString() }}
				</template>
				<template #active_users="{ props: { item } }">
					{{ item.active_users.toString() }}
				</template>
				<template #session_type="{ props: { item } }">
					<span v-if="item.session_type">{{ item.creation_type === 'prebuilt' ?
                        item.creation_type : item.session_type
                    }}</span>
				</template>
				<template #status="{ props: { item } }">
					<span
						class="session__status"
						:class="`session__status--${sessionStatus[item.status]}`"
					>
						{{ sessionStatus[item.status] }}
					</span>
				</template>
				<template #action="{ props: { item } }">
					<div class="session__action">
						<AppButton
							v-if="item.status === 2"
							modifier="small-primary"
							@click="resumeSession(item.id)"
							:disabled="buttonLoading"
						>
							Resume
						</AppButton>

						<AppButton
							v-if="item.status === 1"
							modifier="small-warning"
							@click="pauseSession(item.id)"
							:disabled="buttonLoading"
						>
							Pause
						</AppButton>

						<AppButton
							v-if="item.status === 1 || item.status === 2"
							modifier="small-danger"
							@click="stopSession(item.id)"
							:disabled="buttonLoading"
						>
							Stop
						</AppButton>

						<AppButton
							v-if="item.status === 0"
							modifier="small-danger"
							@click="archiveSession(item.id)"
							:disabled="buttonLoading"
						>
							Delete
						</AppButton>
					</div>
				</template>
			</AppTable>

			<div class="session-list__pagination">
				<p>
					{{`${sessionList.from || ''}-${sessionList.to || ''}`}}
				</p>

				<AppCountPicker
					:placeholder="'Item'"
					v-model="params.count"
					@change="changeParams"
					:disabled="loading"
				/>

				<AppPagination
					v-model="params.page"
					:total="sessionList.last_page"
					@change="changeParams(false)"
					:disabled="loading"
				/>
			</div>
		</LayoutBox>

		<AppPopup
			:visible="confirm.isConfirmation"
			type="warning"
			:title="'Warning'"
			:message="confirm.message"
			@close="closeConfirmation"
		>
			<AppButton
				@click="confirm.confirmAction"
				modifier="small-primary"
				:disabled="buttonLoading"
			>Ok</AppButton>
			<AppButton
				@click="closeConfirmation"
				modifier="small-danger"
				:disabled="buttonLoading"
			>Cancel</AppButton>
		</AppPopup>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import store from '@/store'
import sessionModule from '@/store/session/app.js'

export default {
	data () {
		return {
			sessionStatus: ['end', 'active', 'pause'],
			params: {
				keyword: '',
				order_by: null,
				filter_by: null,
				page: 1,
				count: 10,
                type: ''
			},

			selected: [],

			confirm: {
				isConfirmation: false,
				confirmAction: null,
				confirmMessage: ''
			}
		}
	},

	computed: {
		...mapState('session', [
			'sessionList',
			'loading',
			'buttonLoading'
		]),

		fields () {
			return [
				{ name: 'id', text: 'ID', style: 'width: 50px;' },
				{ name: 'session_name', text: 'Session Name' },
				{ name: 'session_start', text: 'Start Time', style: 'width: 150px;' },
				{ name: 'session_end', text: 'End Time', style: 'width: 150px;' },
				{ name: 'participants', text: 'Users' },
				{ name: 'active_users', text: 'Active Users' },
				{ name: 'session_type', text: 'Type', style: 'text-transform: capitalize;'},
				{ name: 'status', text: 'Status' },
				{ name: 'action', text: 'Action', style: 'width: 10em;' }
			]
		},

		sortOptions () {
			return [
				{text: 'Latest', value: 'latest'},
				{text: 'Oldest', value: 'oldest'},
				{text: 'Session Name', value: 'session_name'}
			]
		},

		filterOptions () {
			return [
				{text: 'Active', value: 'active'},
				{text: 'Inactive', value: 'paused'},
				{text: 'Completed', value: 'inactive'}
			]
		}
	},

	async beforeRouteEnter (to, from, next) {
		if (!store._modulesNamespaceMap['session/']) {
			store.registerModule('session', sessionModule)
		}

		next()
	},

	async created () {
		const query = {...this.$route.query}
		if (query.order_by) query.order_by = this.sortOptions.filter(i => i.value === query.order_by)[0]
		if (query.filter_by) query.filter_by = this.filterOptions.filter(i => i.value === query.filter_by)[0]
		if (query.count && query.count % 10 === 0) query.count = Math.max(Math.min(query.count, 30), 10)
		this.params = {...this.params, ...query}

		this.loadNewData()
	},

	async beforeDestroy () {
		if (this.pollingTimeout) clearTimeout(this.pollingTimeout)
	},

	methods: {
		async loadNewData () {
			this.$store.commit('session/SET_LOADING', true)
			await this.fetchPolling()
			this.$store.commit('session/SET_LOADING', false)
		},

		async fetchPolling () {
			if (this.pollingTimeout) clearTimeout(this.pollingTimeout)
			await this.$store.dispatch('session/getSessionList', this.$route.query)

			this.pollingTimeout = setTimeout(async () => {
				this.fetchPolling()
			}, 10000)
		},

		closeConfirmation () {
			if (this.buttonLoading) return
			this.confirm = {
				isConfirmation: false,
				confirmAction: null,
				confirmMessage: ''
			}
		},

		showConfirmation (message, callback) {
			this.confirm.isConfirmation = true
			this.confirm.message = message
			this.confirm.confirmAction = callback
		},

		async changeParams (resetPage = true) {
			if (resetPage) this.params.page = 1

			const {
				keyword,
				order_by,
				filter_by,
				page,
				count,
                type
			} = this.params

			const newQuery = {}
			if (keyword) newQuery.keyword = keyword
			if (order_by) newQuery.order_by = order_by.value
			if (filter_by) newQuery.filter_by = filter_by.value
			if (page > 1) newQuery.page = page
			if (count > 10) newQuery.count = count
            newQuery.client_id = this.$route.params.clientId
            newQuery.type = type

			this.$router.push({name: 'session', query: newQuery})

			this.loadNewData()
		},

		viewSession () {
            return
            // let clientId = this.$route.params.clientId
			// if (this.buttonLoading) return
			// if (e.creation_type === 'prebuilt' && e.status === 1) this.$router.push({ name: 'session-view-prebuilt', params: { clientId, sessionId: e.id } })
			// else if (e.session_type.toLowerCase() !== 'solution') this.$router.push({ name: 'session-view', params: { clientId, sessionId: e.id, sessionType: e.session_type } })
			// else this.$router.push({ name: 'session-school-solution-view', params: { sessionId: e.id } })
		},

		searchItem () {
			clearTimeout(this.timeout)
			this.selected = []
			this.timeout = setTimeout(() => {
				this.params.page = 1
				this.params.count = 10

				this.changeParams()
			}, 1000)
		},

		appendFormParams (form) {
			const query = {...this.$route.query}
			const {
				keyword,
				order_by,
				filter_by,
				page,
				count,
                client_id,
                type
			} = query

			if (keyword) form.append('keyword', keyword)
			if (order_by) form.append('order_by', order_by)
			if (filter_by) form.append('filter_by', filter_by)
			if (page) form.append('page', page)
			if (count) form.append('count', count)
			if (client_id) form.append('client_id', client_id)
			if (type) form.append('type', type)
			return form
		},

		async resumeSession (ids) {
			let form = new FormData()
			this.appendFormParams(form)
			form.append('id', ids)
            form.append('status', 1)
			await this.$store.dispatch('session/actionSession', form)
			this.selected = []
		},

		async pauseSession (ids) {
			let form = new FormData()
			this.appendFormParams(form)
			form.append('id', ids)
			form.append('status', 2)

			this.showConfirmation(
				'Selected session will be paused. However, paused session can be resumed.',
				async () => {
					this.closeConfirmation()
					await this.$store.dispatch('session/actionSession', form)
					this.selected = []
				})
		},

		stopSession (ids) {
			let form = new FormData()
			form = this.appendFormParams(form)
			form.append('id', ids)
			form.append('status', 0)

			this.showConfirmation(
				'Stopping a session will not be resumed and can be deleted.',
				async () => {
					this.closeConfirmation()
					await this.$store.dispatch('session/stopSession', form)
					this.selected = []
				})
		},

		archiveSession (ids) {
			let form = new FormData()
			form = this.appendFormParams(form)
			form.append('id', ids)

			this.showConfirmation(
				'Deleted session will be archived.',
				async () => {
					this.closeConfirmation()
					await this.$store.dispatch('session/archiveSession', form)
					this.selected = []
				})
		}
	}
}
</script>

<style lang="scss" scoped>
.session-list {
	flex: 1 1 auto;
	padding: 20px;

	&__container {
		height: 100%;
		min-height: 400px;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
	}

	&__title {
		flex: 1 1 auto;
	}

	&__header {
		display: flex;
		align-items: center;
		padding-bottom: 10px;

		> *:not(:last-child) {
			margin-right: 10px;
		}

		.app-dropdown {
			width: 10em;
		}
	}

	&__table {
		flex: 1 1 auto;
		margin-bottom: 20px;
	}

	.session {
		&__status {
			text-transform: capitalize;

			&--end { color: $danger; }
			&--pause { color: $warning; }
			&--active { color: $primary; }
		}

		&__action {
			display: flex;

			> * {
				padding: 3px;
				font-size: .9em;
				&:not(:last-child) {
					margin-right: 10px;
				}
			}
		}
	}

	&__pagination {
		width: 100%;
		display: flex;
		align-items: center;

		> *:first-child { margin-right: auto; }
		> *:not(:first-child) { margin-left: 20px; }
	}
}
</style>
